// Imports for external libraries go here.
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { PhotoEssayProps, AssetProps } from './PhotoEssay.types';
import { PhotoEssayVerticalTabletModalGlobalStyle, StyledPhotoEssay } from './PhotoEssay.styles';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, Image, VideoMolecule } from '@marriott/mi-ui-library';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import clsx from 'clsx';
import { VARIATION_ONE, VARIATION_TWO } from '../../modules/constants/constant';
import { useViewport } from '../../modules/helpers';

interface BrightcoveControlBarChild {
  show?: () => void;
}

interface BrightcoveControlBar {
  getChild?: (name: string) => BrightcoveControlBarChild | undefined;
  addChild?: (name: string, options: Record<string, unknown>) => void;
}

interface PlayerInstance {
  paused: () => boolean;
  pause: () => void;
  play: () => void;
  on: (event: string, callback: () => void) => void;
  controls_?: boolean;
  ready: (callback: () => void) => void;
  getChild?: (name: string) => BrightcoveControlBar | undefined;
}

interface BrightcoveReactPlayerLoaderSuccess {
  ref?: PlayerInstance;
}

// Use named rather than default exports.
export const PhotoEssay: FC<PhotoEssayProps> = (props: PhotoEssayProps) => {
  const variation1 = props?.variation?.toLowerCase() === VARIATION_ONE;
  const variation2 = props?.variation?.toLowerCase() === VARIATION_TWO;
  const playLabel = props?.playLabel?.toUpperCase() ?? 'PLAY';
  const pauseLabel = props?.pauseLabel?.toUpperCase() ?? 'PAUSE';
  const viewport = useViewport();

  const [videoPlaying, setVideoPlaying] = useState<Record<string, boolean>>({});
  const [videoLoaded, setVideoLoaded] = useState<Record<string, boolean>>({});
  const [videoEnded, setVideoEnded] = useState<Record<string, boolean>>({});
  const [videoHasPlayed, setVideoHasPlayed] = useState<Record<string, boolean>>({});
  const playerRefs = useRef<Record<string, PlayerInstance>>({});
  const parentRef = useRef<HTMLDivElement>(null);
  const assetOneRef = useRef<HTMLDivElement>(null);
  const assetTwoRef = useRef<HTMLDivElement>(null);
  const videoMoleculeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const tabletVerticalVideoModalHostRef = useRef<HTMLDivElement | null>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const isTabletModalRange = viewport === 'viewPortM';
  const [tabletVerticalVideoModal, setTabletVerticalVideoModal] = useState<{
    assetName: string;
    asset: AssetProps;
  } | null>(null);

  useEffect(() => {
    if (!isTabletModalRange) return;
    const next: Record<string, boolean> = {};
    if (variation2 && props['assetTwo']?.assetType === 'video') next['assetTwo'] = true;
    if (!variation1 && !variation2 && props['assetOne']?.assetType === 'video') next['assetOne'] = true;
    if (Object.keys(next).length) {
      setVideoLoaded(prev => ({ ...prev, ...next }));
    }
  }, [isTabletModalRange, variation1, variation2, props['assetOne']?.assetType, props['assetTwo']?.assetType]);

  const closeTabletVerticalVideoModal = useCallback(() => {
    setTabletVerticalVideoModal(current => {
      if (current) {
        try {
          playerRefs.current[current.assetName]?.pause();
        } catch {
          /* noop */
        }
        setVideoPlaying(v => ({ ...v, [current.assetName]: false }));
      }
      return null;
    });
  }, []);

  /** VideoMolecule only opens its modal when .video-image-play-btn is clicked; trigger that without changing mi-ui-library Video. */
  useEffect(() => {
    if (!tabletVerticalVideoModal) return;

    const btn = tabletVerticalVideoModalHostRef.current?.querySelector<HTMLButtonElement>('.video-image-play-btn');
    if (!btn) return;

    btn.click();

    const observer = new MutationObserver(() => {
      const modal = document.querySelector<HTMLElement>('.video-modal-main-container');
      if (modal) {
        observer.disconnect();
        const firstFocusable = modal.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        (firstFocusable ?? modal).focus();
        setupTabExitOnModal(modal);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [tabletVerticalVideoModal]);

  /** Clear local state when our portaled player unmounts (close, Escape, etc.) — scoped by Brightcove container id, not all `.video-modal-main-container`s. */
  useEffect(() => {
    if (!tabletVerticalVideoModal) return;

    const { assetName, asset } = tabletVerticalVideoModal;
    const playerDomId = `luxury-photo-essay-vertical-modal-${
      asset.assetProperties?.videoAccountId as string
    }-${assetName}`;

    let seenOurs = false;
    let closeTimeoutId: ReturnType<typeof setTimeout> | undefined;

    const oursIsOpen = () => {
      const node = document.getElementById(playerDomId);
      return !!(node && node.closest('.video-modal-main-container'));
    };

    const syncStateWithDom = () => {
      if (oursIsOpen()) {
        seenOurs = true;
        if (closeTimeoutId) clearTimeout(closeTimeoutId);
        return;
      }
      if (!seenOurs) return;
      if (closeTimeoutId) clearTimeout(closeTimeoutId);
      closeTimeoutId = setTimeout(() => {
        if (!oursIsOpen()) closeTabletVerticalVideoModal();
      }, 100);
    };

    const observer = new MutationObserver(syncStateWithDom);
    observer.observe(document.body, { childList: true, subtree: true });
    syncStateWithDom();

    return () => {
      observer.disconnect();
      if (closeTimeoutId) clearTimeout(closeTimeoutId);
    };
  }, [tabletVerticalVideoModal, closeTabletVerticalVideoModal]);

  useEffect(() => {
    if (!tabletVerticalVideoModal) return;
    document.body.setAttribute('data-photo-essay-vertical-video-modal', 'true');
    return () => document.body.removeAttribute('data-photo-essay-vertical-video-modal');
  }, [tabletVerticalVideoModal]);

  //To align the vertical asset in case of variation 2 and 3
  useEffect(() => {
    const tryMeasure = () => {
      if (!parentRef.current || !assetOneRef.current || !assetTwoRef.current) return;

      const parentHeight = parentRef.current.offsetHeight;
      const h1 = assetOneRef.current.offsetHeight;
      const h2 = assetTwoRef.current.offsetHeight;

      // guard until real heights exist
      if (!parentHeight || !h1 || !h2) {
        !viewport?.includes('Mobile') && requestAnimationFrame(tryMeasure);
        return;
      }

      const minHeight = Math.min(h1, h2);
      const marginValue = `${(parentHeight - minHeight) / 2}px`;

      // apply ONLY to the smaller one
      if (h1 < h2) {
        assetOneRef.current.style.marginTop = marginValue;
      } else if (h2 < h1) {
        assetTwoRef.current.style.marginTop = marginValue;
      }
    };

    !viewport?.includes('Mobile') && tryMeasure();
  }, [viewport]);

  const handleClickEvent = (assetId: string) => {
    const player = playerRefs.current[assetId];
    if (!player) return;
    const isPaused = player.paused();
    if (isPaused) {
      player.play();
    } else {
      player.pause();
    }
    setVideoPlaying(prev => ({
      ...prev,
      [assetId]: isPaused, //true when playing
    }));
  };

  const FOCUSABLE_SELECTOR =
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /** Attaches a one-time Tab-exit listener to the modal. On Tab from the last focusable element
   *  inside the modal, closes the modal via Escape and moves focus to the next page element. */
  const setupTabExitOnModal = (modal: HTMLElement) => {
    const handleTabExit = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || e.shiftKey) return;

      const focusables = Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        el => el.offsetParent !== null
      );
      if (!focusables.length || document.activeElement !== focusables[focusables.length - 1]) return;

      e.preventDefault();
      modal.removeEventListener('keydown', handleTabExit);

      // Compute next page element before the modal is removed from DOM
      const trigger = lastTriggerRef.current;
      const allPageFocusable = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        el => el.offsetParent !== null && !el.closest('.video-modal-main-container')
      );
      const triggerIdx = trigger ? allPageFocusable.indexOf(trigger) : -1;
      const nextEl =
        triggerIdx !== -1 && triggerIdx + 1 < allPageFocusable.length ? allPageFocusable[triggerIdx + 1] : trigger;

      // Close the modal — VideoMolecule handles Escape internally
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));

      // Focus next after modal DOM is removed (modal close is async due to state update)
      if (nextEl) setTimeout(() => nextEl.focus(), 0);
    };

    modal.addEventListener('keydown', handleTabExit);
  };

  //function to track the event when the video opens in modal (square variation and for smaller tablet devices)
  const openTabletVerticalVideoModal = (assetName: string, asset: AssetProps) => {
    lastTriggerRef.current = document.activeElement as HTMLElement;
    setTabletVerticalVideoModal({ assetName, asset });
  };

  // When video molecule play button is clicked, focus is set approriately to elements inside the modal
  const handleVideoMoleculePlayClick = (assetName: string) => {
    lastTriggerRef.current = document.activeElement as HTMLElement;
    const container = videoMoleculeRefs?.current?.[assetName];
    const playBtn = container?.querySelector<HTMLButtonElement>('.video-image-play-btn');
    if (!playBtn) return;

    playBtn.click();

    const observer = new MutationObserver(() => {
      const modal = document.querySelector<HTMLElement>('.video-modal-main-container');
      if (modal) {
        observer.disconnect();
        const firstFocusable = modal.querySelector<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        (firstFocusable ?? modal).focus();
        setupTabExitOnModal(modal);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const createBrightcovePlayerSuccessHandler =
    (assetId: string, isCinemograph: boolean, isVideo: boolean, autoplayVideoOnReady = false) =>
    (success: BrightcoveReactPlayerLoaderSuccess) => {
      const { ref } = success;
      if (!ref) return;

      if (isCinemograph) {
        ref.controls_ = false;
      }

      playerRefs.current[assetId] = ref;

      setVideoLoaded(prev => ({
        ...prev,
        [assetId]: true,
      }));

      if (isCinemograph) {
        ref.play();
        setVideoPlaying(prev => ({ ...prev, [assetId]: true }));
      } else if (isVideo) {
        ref.on('play', () => {
          setVideoPlaying(prev => ({ ...prev, [assetId]: true }));
          setVideoEnded(prev => ({ ...prev, [assetId]: false }));
          setVideoHasPlayed(prev => ({ ...prev, [assetId]: true }));
        });
        ref.on('pause', () => setVideoPlaying(prev => ({ ...prev, [assetId]: false })));
        ref.on('ended', () => {
          setVideoPlaying(prev => ({ ...prev, [assetId]: false }));
          setVideoEnded(prev => ({ ...prev, [assetId]: true }));
        });
        ref.ready(() => {
          try {
            const controlBar = ref.getChild?.('ControlBar');
            if (controlBar) {
              const fullscreenToggle = controlBar.getChild?.('FullscreenToggle');
              if (!fullscreenToggle) {
                controlBar.addChild?.('FullscreenToggle', {});
              } else {
                fullscreenToggle.show?.();
              }
            }
          } catch {
            if (process.env.NODE_ENV === 'development') {
              console.debug('PhotoEssay: Fullscreen toggle not available for this player');
            }
          }
          if (autoplayVideoOnReady) {
            try {
              ref.play();
            } catch {
              /* autoplay may be blocked. */
            }
          }
        });
      }
    };

  const getAsset = (asset: AssetProps, assetName: string) => {
    const isCinemograph = asset?.assetType === 'cinemograph';
    const isVideo = asset?.assetType === 'video';
    const useVideoMolecule = variation1 && isVideo;
    const playVerticalVideoInTabletModal = isTabletModalRange && isVideo && !isCinemograph;
    const options = {
      responsive: true,
      autoplay: isCinemograph ? true : false,
      muted: true,
      loop: isCinemograph ? true : false,
      preload: 'auto',
      src: 'https://players.brightcove.net',
      controls: isCinemograph ? false : true,
    };

    if (useVideoMolecule) {
      return (
        <div
          className="video-cinemograph-container photo-essay-video-molecule-wrapper"
          ref={el => {
            videoMoleculeRefs.current[assetName] = el;
          }}
        >
          <VideoMolecule
            id={`luxury-photo-essay-video-${asset?.assetProperties?.videoAccountId}-${assetName}`}
            accountId={asset?.assetProperties?.videoAccountId as string}
            videoId={asset?.assetProperties?.brightcoveVideoId as string}
            playerId={(asset?.assetProperties?.playerId as string) ?? 'default'}
            poster={asset?.dynamicImage?.assetPath ?? asset?.assetProperties?.videoThumbnail ?? ''}
            enableMute={true}
            enableVideoLoop={false}
            renditions={asset?.dynamicImage?.renditions}
          />
          <Button
            className="custom_click_track d-flex justify-content-center align-items-center icon-play-pause_video"
            id={`${assetName}-play-pause`}
            callback={() => handleVideoMoleculePlayClick(assetName)}
            custom_click_track_value={`${asset?.assetType} | ${playLabel} button | internal`}
            buttonCopy=""
            ariaLabel={playLabel}
          >
            <span className="t-font-s play-pause-btn">{playLabel}</span>
          </Button>
        </div>
      );
    }

    const showMiddlePlayButton =
      isVideo &&
      videoLoaded?.[assetName] &&
      !videoPlaying?.[assetName] &&
      (videoEnded?.[assetName] || !videoHasPlayed?.[assetName]);

    return (
      <>
        {asset?.assetType === 'image' ? (
          <Image
            customClass={'photo-essay-image'}
            dynamic={false}
            defaultImageURL={asset?.dynamicImage?.assetPath}
            renditions={asset?.dynamicImage?.renditions}
            altText={asset?.dynamicImage?.altText}
          />
        ) : (
          <div
            className={clsx(
              'video-cinemograph-container',
              isCinemograph && 'video-cinemograph-container--cinemograph',
              isVideo && 'video-cinemograph-container--custom-poster'
            )}
          >
            {isVideo && asset?.dynamicImage && (
              <div
                className={clsx(
                  'photo-essay-video-poster-overlay',
                  /* Hide cover when play button is hidden - same visibility as middle play button */
                  videoLoaded?.[assetName] && !showMiddlePlayButton && 'photo-essay-video-poster-overlay--hidden'
                )}
              >
                <Image
                  customClass="photo-essay-video-poster-image"
                  dynamic={true}
                  defaultImageURL={asset?.dynamicImage?.assetPath ?? asset?.assetProperties?.videoThumbnail ?? ''}
                  renditions={asset?.dynamicImage?.renditions}
                  altText={asset?.assetProperties?.thumbnailAltText ?? asset?.assetProperties?.altText ?? ''}
                />
              </div>
            )}
            {!playVerticalVideoInTabletModal && (
              <ReactPlayerLoader
                id={`luxury-photo-essay-${asset?.assetProperties?.videoAccountId}-${assetName}`}
                accountId={asset?.assetProperties?.videoAccountId as string}
                videoId={asset?.assetProperties?.brightcoveVideoId as string}
                playerId={asset?.assetProperties?.playerId ?? ('default' as string)}
                options={options}
                onSuccess={createBrightcovePlayerSuccessHandler(assetName, isCinemograph, isVideo)}
              />
            )}
            {videoLoaded?.[assetName] && (showMiddlePlayButton || isCinemograph) && (
              <Button
                className={clsx(
                  'custom_click_track d-flex justify-content-center align-items-center',
                  isVideo ? 'icon-play-pause_video' : 'icon-play-pause_cinemograph'
                )}
                id={`${assetName}-play-pause`}
                callback={() =>
                  playVerticalVideoInTabletModal && isVideo
                    ? openTabletVerticalVideoModal(assetName, asset)
                    : handleClickEvent(assetName)
                }
                custom_click_track_value={`${asset?.assetType} | ${
                  videoPlaying?.[assetName] ? pauseLabel : playLabel
                } button | internal`}
                ariaLabel={videoPlaying[assetName] ? pauseLabel : playLabel}
              >
                {isVideo ? (
                  <span className="t-font-s play-pause-btn">{playLabel}</span>
                ) : (
                  <>
                    {!videoPlaying?.[assetName] && (
                      <>
                        <span className="icon-xs icon-play"></span>
                        <span className="sr-only">{playLabel}</span>
                      </>
                    )}
                    {videoPlaying?.[assetName] && (
                      <>
                        <span className="icon-xs icon-pause"></span>
                        <span className="sr-only">{pauseLabel}</span>
                      </>
                    )}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <>
      <StyledPhotoEssay
        data-testid="luxury-PhotoEssay"
        data-component-name="m-luxury-PhotoEssay"
        className={props?.styleclass}
      >
        <div className="container row justify-content-center my-3" ref={parentRef}>
          {props?.assetOne && (
            <div
              className={clsx(
                'col-12 asset_one pb-3 pb-md-0 px-0 px-md-2',
                variation1
                  ? 'col-md-6 variation_one'
                  : variation2
                  ? 'col-md-9 variation_two'
                  : 'col-md-3 variation_three'
              )}
            >
              <div className="asset-one-container" ref={assetOneRef}>
                {props?.assetOne && getAsset(props?.assetOne, 'assetOne')}
              </div>
            </div>
          )}

          <div
            className={clsx(
              'col-12 asset_two px-0 px-md-2',
              variation1 ? 'col-md-6 variation_one' : variation2 ? 'col-md-3 variation_two' : 'col-md-9 variation_three'
            )}
          >
            <div className="asset-two-container" ref={assetTwoRef}>
              {props?.assetTwo && getAsset(props?.assetTwo, 'assetTwo')}
            </div>
          </div>
        </div>
      </StyledPhotoEssay>

      {tabletVerticalVideoModal && (
        <>
          <PhotoEssayVerticalTabletModalGlobalStyle />
          <div className="photo-essay-vertical-tablet-modal-host" ref={tabletVerticalVideoModalHostRef}>
            <VideoMolecule
              key={`${tabletVerticalVideoModal.assetName}-${tabletVerticalVideoModal.asset.assetProperties?.brightcoveVideoId}`}
              id={`luxury-photo-essay-vertical-modal-${tabletVerticalVideoModal.asset.assetProperties?.videoAccountId}-${tabletVerticalVideoModal.assetName}`}
              accountId={tabletVerticalVideoModal.asset.assetProperties?.videoAccountId as string}
              videoId={tabletVerticalVideoModal.asset.assetProperties?.brightcoveVideoId as string}
              playerId={(tabletVerticalVideoModal.asset.assetProperties?.playerId as string) ?? 'default'}
              poster={
                tabletVerticalVideoModal.asset?.dynamicImage?.assetPath ??
                tabletVerticalVideoModal.asset?.assetProperties?.videoThumbnail ??
                ''
              }
              renditions={tabletVerticalVideoModal.asset?.dynamicImage?.renditions}
              enableMute={true}
              enableVideoLoop={false}
            />
          </div>
        </>
      )}
    </>
  );
};

export const PhotoEssayConfig = {
  emptyLabel: 'LuxuryPhotoEssay',
  isEmpty: () => true,
  resourceType: `mi-aem-luxury-spa/components/content/photo-essay/v1/photo-essay`,
};
//TODO:Need to fix the lint issue for editable component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PhotoEssayEditable = (props: any) => (
  <div data-testid="luxury-PhotoEssay" data-component-name="m-luxury-PhotoEssay">
    <EditableComponent config={PhotoEssayConfig} {...props}>
      <PhotoEssay {...props} />
    </EditableComponent>
  </div>
);
